import { updatePinCodeStatus } from './../../../utils/pincode';
import { hasAssignedLocations } from '../../../utils/funcs';
import { handleShowPinCodeScreen } from '../../../store/slices/authSlice';
import store from '../../../store/storeConfig';
import { getEnvs } from '../../../utils/getEnvs';
import { createPinCodeStatus } from '../../../utils/pincode';
import { saveSession } from '../../../utils/storage';
import { CompanyInfoResponseProps, UserResponseProps } from '../../../types';
import { remoteDB } from '../DBConn';
import { axiosInstanceShoppaBackend } from '../../axios.config';
import { PLATFORMS } from '../../../data/platfroms';
import { Tracking } from '../../../analytics/tracking';
import { EVENTS } from '../../../utils/events/index';
import { captureException } from '../../sentry/capture';

export const shoppaLogin = async (name: string, password: string, navigate: (arg: string) => void) => {
	const { user, companySettings } = getEnvs();

	Tracking.capture('login_attempt', {
		username: name,
		platform: PLATFORMS.shoppa_pos,
	});

	try {
		const response = await axiosInstanceShoppaBackend.post('/user/login', {
			username: name,
			password,
			platform: PLATFORMS.shoppa_pos,
		});

		const userDetails = response?.data as UserResponseProps;

		Tracking.identify(userDetails._id, {
			email: userDetails.profile?.email,
			merchantName: userDetails?.merchantName,
			name: `${userDetails.profile?.lastName} ${userDetails?.profile?.firstName} `,
			flags: userDetails.flags,
		});

		Tracking.group('merchant', userDetails.merchantName, {
			merchantName: userDetails.merchantName,
		});

		Tracking.capture(EVENTS.AUTH.USER_LOGIN, {
			userId: userDetails._id,
			merchantName: userDetails.merchantName,
			platform: PLATFORMS.shoppa_pos,
		});

		const companyInfo = await remoteDB(userDetails?.merchantName).get<CompanyInfoResponseProps>(`merchantInfo`);

		saveSession(user, userDetails);
		saveSession(companySettings, companyInfo);

		const isOwner = userDetails?.roles?.includes('owner');
		const hasNoLocation = !hasAssignedLocations(userDetails);

		const isPassCodeEnabled = userDetails?.flags?.passcodeEnabled;

		const isDisabled = userDetails?.flags?.accountLocked;

		if (isDisabled) {
			Tracking.capture('login_failed', {
				reason: 'account_locked',
				userId: userDetails._id,
			});
			throw new Error('Account is currently disabled');
		}

		if (!isPassCodeEnabled) {
			Tracking.capture('pincode_not_enabled', {
				userId: userDetails._id,
			});

			navigate('/passcode');
			window.location.reload();
			return;
		}

		if (isPassCodeEnabled) {
			Tracking.capture('pincode_required', {
				userId: userDetails._id,
			});

			if (!isOwner || (isOwner && !hasNoLocation)) {
				await createPinCodeStatus(false);
				await updatePinCodeStatus(false);
			}

			if (isOwner) navigate('/select-location');

			if (!isOwner) navigate('/terminal');

			window.location.reload();

			return;
		}

		Tracking.capture('login_unknown_state', {
			userId: userDetails._id,
		});

		window.location.reload();

		throw new Error('Something went wrong');
	} catch (err: any) {
		captureException(err, { fn: 'shoppaLogin', args: { username: name } });
		Tracking.capture('login_failed', {
			username: name,
			error: err?.data?.message ?? err?.message ?? 'Unknown error',
		});
		throw new Error(err?.data?.message ?? err?.message ?? 'An error occured');
	}
};
